import { makeAutoObservable } from 'mobx'
import { store } from './store'
import { NavigationFilter } from '../models/Filter'
import { Post } from '../models/Post'
import { AuctionFilter } from '../models/modes'
import { AuctionStatus } from '../models/Auction'

export default class NavigationStore {
  constructor() {
    makeAutoObservable(this)
  }

  filterAuctionList = (navigationFilter: NavigationFilter) => {
    const [minRooms, maxRooms] = this.translateRooms(navigationFilter.rooms)
    const [minPrice, maxPrice] = this.translatePrice(navigationFilter.priceRange)
    const { originalAuctions } = store.auctionListStore

    const filtersActive =
      navigationFilter.city || navigationFilter.rooms || navigationFilter.priceRange

    const filteredAuctions = filtersActive
      ? originalAuctions.filter((auction: any) => {
          const { rooms: auctionRooms, city } = auction.post!
          const { currentPrice } = auction

          const cityMatch =
            !navigationFilter.city || (city && this.isStringIncluded(city, navigationFilter.city))

          return (
            cityMatch &&
            auctionRooms >= minRooms &&
            (maxRooms === undefined || auctionRooms <= maxRooms) &&
            currentPrice >= minPrice &&
            (maxPrice === undefined || currentPrice <= maxPrice)
          )
        })
      : originalAuctions

    // Sorting the auctions by starting date and deadline
    filteredAuctions.sort((a, b) => {
      if (navigationFilter.sortBy === AuctionFilter.active) {
        const isStatusA = a.status === AuctionStatus.Ongoing
        const isStatusB = b.status === AuctionStatus.Ongoing

        if (isStatusA && !isStatusB) {
          return -1
        } else {
          return 1
        }
      }
      if (navigationFilter.sortBy === AuctionFilter.finished) {
        const isFinishedA =
          a.status === AuctionStatus.Finished || a.status === AuctionStatus.FirstWinsFinished
        const isFinishedB =
          b.status === AuctionStatus.Finished || b.status === AuctionStatus.FirstWinsFinished

        if (isFinishedA && !isFinishedB) {
          return -1
        } else {
          return 1
        }
      }
      // This is the default case for upcoming Auctions
      const hasStartedA = a.status !== AuctionStatus.Pending
      const hasStartedB = b.status !== AuctionStatus.Pending

      if (!hasStartedA && hasStartedB) {
        return -1 // Upcoming auctions come first
      } else if (hasStartedA && !hasStartedB) {
        return 1 // Upcoming auctions come first
      } else {
        const isActiveA = a.status === AuctionStatus.Ongoing
        const isActiveB = b.status === AuctionStatus.Ongoing

        if (isActiveA && !isActiveB) {
          return -1 // Active auctions come next
        } else if (!isActiveA && isActiveB) {
          return 1 // Active auctions come next
        } else {
          const isFinishedA =
            a.status === AuctionStatus.Finished || a.status === AuctionStatus.FirstWinsFinished
          const isFinishedB =
            b.status === AuctionStatus.Finished || b.status === AuctionStatus.FirstWinsFinished

          if (isFinishedA && !isFinishedB) {
            return -1 // Finished auctions come last
          } else {
            return 1 // Finished auctions come last
          }
        }
      }
    })

    store.auctionListStore.auctionRegistry.clear()
    filteredAuctions.forEach((auction: any) => {
      store.auctionListStore.auctionRegistry.set(auction.id, auction)
    })
  }

  filterPostList = (navigationFilter: NavigationFilter) => {
    const [minRooms, maxRooms] = this.translateRooms(navigationFilter.rooms)
    const [minPrice, maxPrice] = this.translatePrice(navigationFilter.priceRange)
    const { originalPosts } = store.postListStore

    const filtersActive =
      navigationFilter.city || navigationFilter.rooms || navigationFilter.priceRange

    const filteredPosts = filtersActive
      ? originalPosts.filter((post: any) => {
          const { city } = post
          const { rooms: postRooms, price } = post

          const cityMatch =
            !navigationFilter.city || (city && this.isStringIncluded(city, navigationFilter.city))

          return (
            cityMatch &&
            postRooms >= minRooms &&
            (maxRooms === undefined || postRooms <= maxRooms) &&
            price >= minPrice &&
            (maxPrice === undefined || price <= maxPrice)
          )
        })
      : originalPosts

    store.postListStore.postRegistry.clear()
    filteredPosts.forEach((post: any) => {
      store.postListStore.postRegistry.set(post.id, post)
    })
  }

  isStringIncluded = (str: string | null, substring: string): boolean =>
    str ? str.toLowerCase().includes(substring.toLowerCase()) : true

  translateRooms = (rooms: string): [number, number] | [number] => {
    const roomRanges: { [key: string]: [number, number] | [number] } = {
      '1-2': [1, 2],
      '2-4': [2, 4],
      '4-6': [4, 6],
      '6-10': [6, 10],
      '10-20': [10, 20],
      '> 20': [21],
    }

    return roomRanges[rooms] || [0]
  }

  translatePrice = (price: string): [number, number] | [number] => {
    const priceRanges: { [key: string]: [number, number] | [number] } = {
      '0-50,000': [0, 50000],
      '50,000-100,000': [50000, 100000],
      '100,000-200,000': [100000, 200000],
      '200,000-500,000': [200000, 500000],
      '500,000-1,000,000': [500000, 1000000],
      '> 1,000,000': [1000000],
    }

    return priceRanges[price] || [0]
  }

  latestSortFilter = <T extends { id: any }>(items: T[]) => {
    const sortedItems = items
      .filter((item) => store.logsStore.logs.some((log) => log.typeId === item.id))
      .sort((a, b) => {
        const itemA = store.logsStore.logs.find((log) => log.typeId === a.id)
        const itemB = store.logsStore.logs.find((log) => log.typeId === b.id)

        if (itemA && itemB) {
          return new Date(itemB.date).getTime() - new Date(itemA.date).getTime()
        }
        if (itemA) {
          return -1
        }
        if (itemB) {
          return 1
        }

        return 0
      })

    return sortedItems
  }

  popularCitiesFilter = (posts: Post[]): string[] => {
    const cityCountMap: { [city: string]: number } = {}

    posts.forEach((post) => {
      const { city } = post
      if (cityCountMap[city]) {
        cityCountMap[city]++
      } else {
        cityCountMap[city] = 1
      }
    })

    const cityCountArray = Object.entries(cityCountMap).map(([city, count]) => ({
      city,
      count,
    }))

    cityCountArray.sort((a, b) => b.count - a.count)

    const popularCities: string[] = []
    const addedCities: { [city: string]: boolean } = {}

    for (const cityCount of cityCountArray) {
      if (popularCities.length >= 15) {
        break
      }

      const { city } = cityCount
      if (!addedCities[city]) {
        popularCities.push(city)
        addedCities[city] = true
      }
    }

    return popularCities
  }
}
